/**
 * Single service worker registration — shared by PWA shell, FCM, and background sync.
 */
import { Capacitor } from '@capacitor/core';
import { isPublicSiteRoute } from '../routes/appRoutes';

let registrationPromise = null;

export function getServiceWorkerUrl() {
  return `${process.env.PUBLIC_URL || ''}/service-worker.js`;
}

/** Remove stale SW in dev so reload / hot refresh stay reliable. */
async function unregisterDevServiceWorkers() {
  if (process.env.NODE_ENV === 'production') return;
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((reg) => reg.unregister()));
  } catch (err) {
    console.warn('serviceWorker.unregister (dev):', err);
  }
}

function hasPendingTicketIntent() {
  try {
    if (sessionStorage.getItem('lockedin_pending_ticket_code')) return true;
    if (localStorage.getItem('lockedin_pending_ticket_ls')) return true;
  } catch (_) {}
  return false;
}

export function registerServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }
  if (Capacitor.isNativePlatform()) {
    return Promise.resolve(null);
  }
  if (typeof window !== 'undefined') {
    const path = window.location.pathname || '/';
    if (path.indexOf('/ticket/') === 0) {
      console.log('[ticket-sw] registration skipped on ticket route');
      return Promise.resolve(null);
    }
    try {
      if (hasPendingTicketIntent()) {
        console.log('[ticket-sw] registration skipped — pending ticket');
        return Promise.resolve(null);
      }
    } catch (_) {}
    // Keep the SW scoped to the app, not the public marketing site.
    if (isPublicSiteRoute(path)) {
      console.log('[pwa-sw] registration skipped on public marketing route');
      return Promise.resolve(null);
    }
  }
  if (process.env.NODE_ENV !== 'production') {
    return unregisterDevServiceWorkers().then(() => null);
  }
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(getServiceWorkerUrl())
      .then((registration) => {
        if (registration) scheduleUpdateChecks(registration);
        return registration;
      })
      .catch((err) => {
        registrationPromise = null;
        console.warn('serviceWorker.register:', err);
        return null;
      });
  }
  return registrationPromise;
}

let updateChecksScheduled = false;

/**
 * Keep installed PWAs current. iOS/Android PWAs can run for days without a hard
 * reload, so we proactively ask the browser to re-fetch service-worker.js on
 * load, when the app regains focus, and on a periodic timer. When the bytes
 * change (SW_BUILD bump) a new worker installs, skipWaiting() activates it, and
 * the controllerchange handler in index.js reloads the page onto the new build.
 */
function scheduleUpdateChecks(registration) {
  if (updateChecksScheduled) return;
  updateChecksScheduled = true;

  const runUpdate = () => {
    registration.update().catch((err) => {
      console.warn('[pwa-sw] update check failed:', err?.message || err);
    });
  };

  registration.addEventListener('updatefound', () => {
    const incoming = registration.installing;
    if (!incoming) return;
    console.log('[pwa-sw] new service worker found — installing latest build');
    incoming.addEventListener('statechange', () => {
      if (incoming.state === 'installed' && navigator.serviceWorker.controller) {
        console.log('[pwa-sw] new build installed — activating');
      }
    });
  });

  // Check shortly after boot, when the app is refocused/reshown, and hourly.
  runUpdate();
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') runUpdate();
    });
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', runUpdate);
  }
  setInterval(runUpdate, 60 * 60 * 1000);
}

export async function getServiceWorkerRegistration() {
  const reg = await registerServiceWorker();
  if (reg) return reg;
  return navigator.serviceWorker?.ready ?? null;
}
